import React, { memo } from "react";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "../../assets/Footer.png";

const Footer = memo(() => {
  const productLinks = [
    { name: "Slip On Flange", href: "/products/slip-on" },
    { name: "Weld Neck Flange", href: "/products/weld-neck" },
    { name: "Blind Flange", href: "/products/blind" },
    { name: "Long Weld Neck", href: "/products/long-weld" },
    { name: "Threaded Flanges", href: "/products/threaded" },
    { name: "Socket Weld Flange", href: "/products/socket-weld" },
    { name: "Lap Joint Flange", href: "/products/lap-joint" },
    { name: "Companion Flange", href: "/products/plate" },
    { name: "Studding Outlet Flange", href: "/products/rf-rij" },
  ];

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/aboutus" },
    { name: "All Products", href: "/products" },
    { name: "Quality Control", href: "/quality-control" },
    { name: "Contact Us", href: "/contact-us" },
  ];

  return (
    <footer className="w-full bg-[#1c1d1f] text-gray-300 font-sans border-t border-gray-800">
      {/* Top Accent Line */}
      <div className="w-full h-1 bg-amber-600" />

      {/* ─── MOBILE FOOTER (Screens < md) ─── */}
      <div className="block md:hidden px-4 py-8">
        {/* Brand & Brief Description */}
        <div className="mb-6 space-y-2.5">
          <Link to="/" className="inline-block" style={{ display: "inline-block", maxWidth: "150px" }}>
            <img
              src={Logo}
              alt="Auric Engineering"
              style={{
                height: "50px",
                maxHeight: "50px",
                width: "auto",
                maxWidth: "130px",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Link>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Leading industrial manufacturer of forged steel flanges, pipe fittings, and piping components in Rajkot, India.
          </p>
        </div>

        {/* 2-Column Links Grid on Mobile */}
        <div className="grid grid-cols-2 gap-5 py-5 border-y border-gray-800/80 mb-6">
          {/* Quick Links */}
          <div>
            <h4 className="text-[11px] font-bold text-amber-500 uppercase tracking-wider mb-2.5">
              Navigation
            </h4>
            <ul className=" text-[11px]">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.href}
                    className="hover:text-amber-400 text-gray-300 transition-colors flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-2.5 h-2.5 text-amber-500/80 shrink-0" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-[11px] font-bold text-amber-500 uppercase tracking-wider mb-2.5">
              Products
            </h4>
            <ul className="text-[11px]">
              {productLinks.slice(0, 5).map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.href}
                    className="hover:text-amber-400 text-gray-300 transition-colors flex items-center gap-1.5 truncate"
                  >
                    <ArrowRight className="w-2.5 h-2.5 text-amber-500/80 shrink-0" />
                    <span className="truncate">{link.name}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/products"
                  className="text-amber-400 font-semibold hover:underline inline-flex items-center gap-1 pt-0.5"
                >
                  <span>View All ({productLinks.length})</span>
                  <ArrowRight className="w-2.5 h-2.5" />
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Direct Contact Info Strip on Mobile */}
        <div className="space-y-3 text-[11px] text-gray-400 mb-6 bg-white/5 p-3.5 rounded-lg border border-white/5">
          <div className="flex items-start gap-2.5 text-gray-300">
            <MapPin className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span className="leading-snug">Udgam School, 3, Auric Engineering, 80 Feet Rd, Rajkot, Gujarat 360004</span>
          </div>
          <div>
            <a
              href="tel:+917434905789"
              className="inline-flex items-center gap-2.5 text-gray-200 hover:text-white transition-colors font-medium"
            >
              <Phone className="w-4 h-4 text-amber-500 shrink-0" />
              <span>+91 7434905789</span>
            </a>
          </div>
          <div>
            <a
              href="mailto:info@auriceng.in"
              className="inline-flex items-center gap-2.5 text-gray-200 hover:text-white transition-colors font-medium"
            >
              <Mail className="w-4 h-4 text-amber-500 shrink-0" />
              <span>info@auriceng.in</span>
            </a>
          </div>
        </div>

        {/* Mobile Copyright */}
        <div className="pt-3 border-t border-gray-800 text-center text-[10px] text-gray-500 space-y-1">
          <p>© 2026 Auric Engineering Pvt Ltd. All rights reserved.</p>
        </div>
      </div>

      {/* ─── DESKTOP FOOTER (Screens >= md) ─── */}
      <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Column 1: Company Profile (4 cols) */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" className="inline-block">
              <img
                src={Logo}
                alt="Auric Engineering"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-xs text-gray-400 leading-relaxed max-w-sm">
              Auric Engineering Pvt Ltd is a leading industrial manufacturer of forged steel flanges, pipe fittings, and high-pressure piping components engineered for global energy, petrochemical, and construction service.
            </p>
          </div>

          {/* Column 2: Quick Links (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider border-b border-gray-800 pb-2">
              Quick Navigation
            </h3>
            <ul className="space-y-2 text-xs">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.href}
                    className="hover:text-amber-400 transition-colors inline-flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-3 h-3 text-amber-500" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Products (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider border-b border-gray-800 pb-2">
              Products
            </h3>
            <ul className="space-y-2 text-xs">
              {productLinks.map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.href}
                    className="hover:text-amber-400 transition-colors inline-flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-3 h-3 text-amber-500" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact & Address (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider border-b border-gray-800 pb-2">
              Contact Information
            </h3>
            <div className="space-y-3 text-xs text-gray-400">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>Udgam School, 3,Auric Engineering Aadarsh City -A Beside Punit Nagar, 80 Feet Rd, B/h, Rajkot, Gujarat 360004</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <a href="tel:04023353898" className="hover:text-white transition-colors">
                  +91 7434905789
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <a href="mailto:info@auriceng.in" className="hover:text-white transition-colors">
                  info@auriceng.in
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright Strip */}
        <div className="mt-12 pt-6 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-500">
          <p>© 2026 Auric Engineering Pvt Ltd. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/aboutus" className="hover:text-gray-300 transition-colors">
              About Us
            </Link>
            <Link to="/contact-us" className="hover:text-gray-300 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";
export default Footer;